import React from "react";

import { Icon } from "@/components/atoms/Icon";
import { icons } from "@/objects/@icons";
import { Typography } from "@/components/ui/typography";
import { Button } from "@/components/ui/button";
import { cn } from "@/components/ui/utils";
import { useSelectContext } from "./Select.hooks";

/**
 * The visible trigger button for a Select.
 *
 * Render the selected label (or nothing) as children — a placeholder is shown
 * automatically when children is absent/null.
 *
 * The Popper wrapping this element handles the click-to-open behaviour, so no
 * onClick is needed here.
 */
export function SelectTrigger({
  children,
  placeholder = "Select…",
  iconOnly = false,
  className,
}: {
  children?: React.ReactNode;
  placeholder?: string;
  iconOnly?: boolean;
  className?: string;
}) {
  const { size } = useSelectContext();
  return (
    <Button
      variant="outline"
      size={size === "md" ? "default" : size}
      className={cn(
        iconOnly ? "w-auto" : "w-full",
        iconOnly ? "justify-center" : "justify-between",
        iconOnly && "min-w-0",
        className,
      )}
    >
      {!iconOnly && (
        <div className="flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
          {children ?? (
            <Typography
              level="body-sm"
              className="text-muted-foreground/80 italic"
            >
              {placeholder}
            </Typography>
          )}
        </div>
      )}
      <Icon ReactIcon={icons.chevronDown} size="sm" />
    </Button>
  );
}
